import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  MenuItem,
  Chip,
  Autocomplete,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { sessionTypes } from '../../mockValue';
import apiService from '../../services/api';
import { NumberInputWrapper } from '../../styles/globalStyle';
import { toast } from 'react-toastify';
import FormTextField, { RequiredLabel } from '../common/FormTextField';

interface SessionEditDialogProps {
  open: boolean;
  session: any | null;
  onClose: () => void;
  onSave: () => void;
}

interface FormData {
  sessionTitle: string;
  sessionDescription?: string;
  sessionType: string;
  venue?: string;
  sessionDate?: string;
  startTime: string | dayjs.Dayjs | null;
  endTime: string | dayjs.Dayjs | null;
  capacity?: number;
  registrationRequired: boolean;
  isFeatured: boolean;
  sessionTags: string[];
  speakers: string[];
}

const SessionEditDialog: React.FC<SessionEditDialogProps> = ({
  open,
  session,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [speakerOptions, setSpeakerOptions] = useState<
    { id: string; name: string }[]
  >([]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      sessionTitle: '',
      sessionDescription: '',
      sessionType: '',
      venue: undefined,
      sessionDate: '',
      startTime: '',
      endTime: '',
      capacity: undefined,
      registrationRequired: false,
      isFeatured: false,
      sessionTags: [],
      speakers: [],
    },
  });

  useEffect(() => {
    if (session && open) {
      // fill the form fields
      const speakerIds = Array.isArray(session.speakers)
        ? session.speakers.map((sp: any) => sp.guestSpeaker?.id)
        : [];
      const presetTags = Array.isArray(session.sessionTags)
        ? session.sessionTags
        : [];

      reset({
        sessionTitle: session.sessionTitle || '',
        sessionDescription: session.sessionDescription || '',
        sessionType: session.sessionType || '',
        venue: session.venue,
        sessionDate: session.sessionDate || '',
        startTime: session.startTime ? dayjs(session.startTime, 'HH:mm') : null,
        endTime: session.endTime ? dayjs(session.endTime, 'HH:mm') : null,
        capacity: session.capacity || undefined,
        registrationRequired: session.registrationRequired || false,
        isFeatured: session.isFeatured || false,
        sessionTags: presetTags,
        speakers: speakerIds,
      });

      setTags(presetTags);
      setTagsInput('');
    } else if (!session && open) {
      reset({
        sessionTitle: '',
        sessionDescription: '',
        sessionType: '',
        venue: '',
        sessionDate: '',
        startTime: '',
        endTime: '',
        capacity: undefined,
        registrationRequired: false,
        isFeatured: false,
        sessionTags: [],
        speakers: [],
      });
      setTags([]);
      setTagsInput('');
    }
  }, [session, open, reset]);

  //////////speaker fetch part/////////////////
  useEffect(() => {
    let isMounted = true;

    const loadSpeakers = async () => {
      try {
        const res = await apiService.getGuestSpeakers();
        const list = Array.isArray(res.data) ? res.data : [];

        const speakers: { id: string; name: string }[] = list.map((s: any) => {
          const first = s.firstName || s.first_name || s.givenName || '';
          const last =
            s.lastName || s.last_name || s.familyName || s.surname || '';

          return {
            id: s.id,
            name:
              `${first} ${last}`.trim() ||
              s.name ||
              s.title ||
              `Speaker ${s.id}`,
          };
        });

        if (isMounted) setSpeakerOptions(speakers);
      } catch (error) {
        console.error('Failed to load speakers', error);
      }
    };

    loadSpeakers();
    return () => {
      isMounted = false;
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    try {
      const formattedData = {
        sessionTitle: data.sessionTitle.trim(),
        sessionDescription: data.sessionDescription?.trim(),
        sessionType: data.sessionType.trim(),
        venue: data.venue ? data.venue.trim() : '',
        sessionDate: data.sessionDate,
        startTime: data.startTime
          ? dayjs(data.startTime).format('HH:mm:ss')
          : undefined,
        endTime: data.endTime
          ? dayjs(data.endTime).format('HH:mm:ss')
          : undefined,
        capacity: data.capacity ? Number(data.capacity) : undefined,
        registrationRequired: data.registrationRequired,
        isFeatured: data.isFeatured,
        sessionTags: tags,
        speakerIds: data.speakers || [],
      };

      if (session) {
        await apiService.updateSession(session.id, formattedData);
        toast.success('Conference Session updated successfully');
      } else {
        await apiService.createSession(formattedData);
        toast.success('Conference Session created successfully');
      }

      onSave();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save session');
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            {session ? 'Edit Session' : 'Create New Session'}
          </Typography>
          {session && (
            <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {session.sessionTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent
          dividers
          sx={{
            maxHeight: { xs: '70vh', sm: '75vh', md: '80vh' },
            overflowY: 'auto',
            p: { xs: 2, sm: 3 },
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          <Grid container spacing={3}>
            {/* session name */}
            <Grid item xs={12}>
              <FormTextField
                name="sessionTitle"
                control={control}
                label={<RequiredLabel label="Session Title" />}
                rules={{
                  required: 'Session title is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <FormTextField
                name="sessionDescription"
                control={control}
                label={<RequiredLabel label="Session Description" />}
                fullWidth
                multiline
                rows={4}
                rules={{
                  required: 'Session Description is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters',
                  },
                  maxLength: {
                    value: 100,
                    message: 'Maximum 100 character',
                  },
                }}
              />
            </Grid>

            {/* Type */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="sessionType"
                control={control}
                label={<RequiredLabel label="Session Type" />}
                select
                rules={{ required: 'Session type is required' }}
              >
                {sessionTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            {/* Venue */}
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="venue"
                control={control}
                label={<RequiredLabel label="Venue" />}
                rules={{
                  required: 'Venue is required',
                  minLength: { value: 3, message: 'Minimum 3 characters' },
                }}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={4}>
              <FormTextField
                name="sessionDate"
                control={control}
                label={<RequiredLabel label="Session Date" />}
                type="date"
                InputLabelProps={{ shrink: true }}
                rules={{ required: 'Session date is required' }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0],
                }}
              />
            </Grid>

            {/* Time */}
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="startTime"
                  control={control}
                  rules={{ required: 'Start time required' }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="Start Time" />}
                      {...field}
                      ampm={true}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Enter start time"
                          error={!!errors.startTime}
                          helperText={errors.startTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Controller
                  name="endTime"
                  control={control}
                  rules={{
                    required: 'End time required',
                    validate: value => {
                      if (!value) return 'End time required';

                      const st = dayjs(getValues('startTime'));
                      const et = dayjs(value);

                      return (
                        et.isAfter(st) || 'End time must be after start time'
                      );
                    },
                  }}
                  render={({ field }) => (
                    <TimePicker
                      label={<RequiredLabel label="End Time" />}
                      ampm={true}
                      {...field}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="Enter end time"
                          error={!!errors.endTime}
                          helperText={errors.endTime?.message}
                        />
                      )}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            {/* Capacity */}
            <Grid item xs={12} sm={6}>
              <NumberInputWrapper>
                <FormTextField
                  name="capacity"
                  control={control}
                  label={<RequiredLabel label="Capacity" />}
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    onInput: (e: React.FormEvent<HTMLInputElement>) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /[^0-9]/g,
                        ''
                      );
                    },
                  }}
                  rules={{
                    required: 'Capacity is required',
                    valueAsNumber: true,
                    validate: (value: number) =>
                      value > 0 || 'Capacity must be positive',
                  }}
                />
              </NumberInputWrapper>
            </Grid>

            {/* Switches */}
            <Grid item xs={12} sm={6}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Controller
                  name="registrationRequired"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Registration Required"
                    />
                  )}
                />

                <Controller
                  name="isFeatured"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Featured Session"
                    />
                  )}
                />
              </Box>
            </Grid>

            {/* autocomplete speaker */}
            <Grid item xs={12}>
              <Controller
                name="speakers"
                control={control}
                defaultValue={[]}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    multiple
                    options={speakerOptions}
                    value={speakerOptions.filter(x =>
                      field.value.includes(x.id)
                    )}
                    onChange={(_, selectedSpeakers) =>
                      field.onChange(selectedSpeakers.map(s => s.id))
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={option => option.name}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Speakers"
                        placeholder="Select speakers"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Tags */}
            <Grid item xs={12}>
              <Controller
                name="sessionTags"
                control={control}
                render={({ field }) => (
                  <Box>
                    {/* Input Field */}
                    <TextField
                      label="Enter Session Tag"
                      placeholder="Type a tag and press Enter"
                      fullWidth
                      value={tagsInput}
                      onChange={e => setTagsInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = tagsInput.trim();
                          if (!value) return;

                          // avoid duplicates
                          if (tags.includes(value)) {
                            setTagsInput('');
                            return;
                          }

                          const updated = [...tags, value];
                          setTags(updated);
                          field.onChange(updated);
                          setTagsInput('');
                        }
                      }}
                    />

                    {/* Chips */}
                    <Box
                      mt={1}
                      sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                    >
                      {tags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          onDelete={() => {
                            const updated = tags.filter(t => t !== tag);
                            setTags(updated);
                            field.onChange(updated);
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>

          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? (
              <CircularProgress size={16} />
            ) : session ? (
              'Update Session'
            ) : (
              'Create Session'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SessionEditDialog;
